// AI-Powered Warranty Chat Assistant - Revolutionary Design
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Warranty, WarrantyRequest } from '../types/warranty';
import { warrantyService } from '../services/warranty';
import { documentService, type ExtractionResult } from '../services/document';
import { authService } from '../services/auth';
import '../styles/components/ChatAssistant.css';

// Speech Recognition types
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: {
      new (): SpeechRecognition;
    };
    webkitSpeechRecognition?: {
      new (): SpeechRecognition;
    };
  }
}

// Cache busting utility for icons

declare global {
  interface Window {
    SpeechRecognition?: {
      new (): SpeechRecognition;
    };
    webkitSpeechRecognition?: {
      new (): SpeechRecognition;
    };
  }
}

// Cache busting utility for icons
const getCacheBustedIconUrl = (iconName: string) => {
  if (import.meta.env.DEV) {
    const timestamp = Date.now();
    return `/icons/${iconName}.svg?v=${timestamp}`;
  }
  return `/icons/${iconName}.svg`;
};

// Icon component with fallback
const Icon = ({ name, className = "" }: { name: string; className?: string }) => {
  const [iconError, setIconError] = React.useState(false);
  
  const handleIconError = () => {
    console.error(`Failed to load icon: ${name}.svg`);
    setIconError(true);
  };

  // If icon failed to load, show a simple colored div as fallback
  if (iconError) {
    return (
      <div 
        className={`icon-fallback ${className}`}
        style={{
          width: '18px',
          height: '18px',
          backgroundColor: '#ff6b35',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          color: 'white',
          fontWeight: 'bold'
        }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <img 
      src={getCacheBustedIconUrl(name)} 
      alt="" 
      className={`icon ${className}`}
      onError={handleIconError}
    />
  );
};

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isProcessing?: boolean;
  attachments?: Array<{
    type: 'image' | 'file';
    name: string;
    url: string;
    size?: number;
  }>;
}

interface ExtractedData {
  productName?: string;
  brand?: string;
  store?: string;
  purchaseDate?: string;
  warrantyPeriod?: string;
  category?: string;
  tags?: string[];
  confidence: number;
  source: 'text' | 'voice' | 'image' | 'file' | 'manual';
}

const AddWarrantyPage: React.FC = () => {
  const navigate = useNavigate();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'system',
      content: '🙏 **வணக்கம்! Welcome to சுவடி AI Assistant!** ✨\n\n🎉 **Your smart warranty companion is here!** I can help you add warranties effortlessly:\n\n💬 **Chat Naturally** - "Bought Samsung Galaxy M31 at Reliance Digital today, 2 years warranty"\n\n🎤 **Voice Commands** - Just speak naturally, I understand!\n\n📷 **Smart Camera** - Snap photos of receipts or warranty cards\n\n📄 **Document Upload** - Drop files and I\'ll extract everything automatically\n\n✨ **Magic happens here!** Let\'s make warranty management delightful! 🚀\n\n**Ready to start? Tell me about your purchase!** 💪',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showManualPopup, setShowManualPopup] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [extractedForConfirmation, setExtractedForConfirmation] = useState<ExtractedData | null>(null);
  
  // Manual form states
  const [manualForm, setManualForm] = useState({
    productName: '',
    brand: '',
    modelNumber: '',
    purchaseDate: '',
    purchaseStore: '',
    warrantyPeriod: '',
    category: '',
    notes: ''
  });
  
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-IN';

        recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
          const last = event.results.length - 1;
          const text = event.results[last][0].transcript;
          
          if (event.results[last].isFinal) {
            setInputText(text);
            setIsListening(false);
            handleSendMessage(text, 'voice');
          }
        };

        recognitionRef.current.onerror = () => {
          setIsListening(false);
        };
      }
    }
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Close add menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.add-menu-container')) {
        setShowAddMenu(false);
      }
    };

    if (showAddMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAddMenu]);

  // Simulate AI processing and data extraction
  const processNaturalLanguage = async (text: string, source: 'text' | 'voice' | 'image' | 'file' | 'manual'): Promise<ExtractedData> => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const words = text.toLowerCase();
    
    const extracted: ExtractedData = {
      confidence: source === 'text' || source === 'voice' ? 0.85 : 0.92,
      source
    };

    // Enhanced brand detection
    if (words.includes('samsung')) {
      extracted.brand = 'Samsung';
      extracted.category = 'electronics';
      extracted.tags = ['smartphone', 'mobile', 'electronics'];
    } else if (words.includes('apple') || words.includes('iphone')) {
      extracted.brand = 'Apple';
      extracted.category = 'electronics';
      extracted.tags = ['smartphone', 'mobile', 'premium'];
    } else if (words.includes('lg')) {
      extracted.brand = 'LG';
      extracted.category = words.includes('tv') || words.includes('television') ? 'electronics' : 'appliances';
    }

    // Product name extraction
    if (words.includes('galaxy m31')) {
      extracted.productName = 'Galaxy M31';
    } else if (words.includes('iphone')) {
      const matches = words.match(/iphone\s*(\d+)/);
      extracted.productName = matches ? `iPhone ${matches[1]}` : 'iPhone';
    }

    // Store detection
    if (words.includes('reliance') || words.includes('digital')) {
      extracted.store = 'Reliance Digital';
    } else if (words.includes('amazon')) {
      extracted.store = 'Amazon';
    } else if (words.includes('flipkart')) {
      extracted.store = 'Flipkart';
    }

    // Enhanced date detection
    if (words.includes('today')) {
      extracted.purchaseDate = new Date().toISOString().split('T')[0];
    } else if (words.includes('yesterday')) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      extracted.purchaseDate = yesterday.toISOString().split('T')[0];
    } else if (words.includes('last week')) {
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      extracted.purchaseDate = lastWeek.toISOString().split('T')[0];
    }

    // Warranty period
    if (words.includes('2 year') || words.includes('two year')) {
      extracted.warrantyPeriod = '2 years';
    } else if (words.includes('1 year') || words.includes('one year')) {
      extracted.warrantyPeriod = '1 year';
    } else if (words.includes('3 year') || words.includes('three year')) {
      extracted.warrantyPeriod = '3 years';
    }

    return extracted;
  };

  const handleSendMessage = async (message?: string, source: 'text' | 'voice' | 'image' | 'file' | 'manual' = 'text') => {
    const text = message || inputText;
    if (!text.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      const extracted = await processNaturalLanguage(text, source);
      setExtractedData(extracted);

      const sourceEmoji = {
        text: '💬',
        voice: '🎤',
        image: '📷',
        file: '📄',
        manual: '✍️'
      };

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `Excellent! ${sourceEmoji[source]} I've processed your ${source === 'voice' ? 'voice command' : source === 'image' ? 'image' : source === 'file' ? 'document' : 'message'} with **${Math.round(extracted.confidence * 100)}% confidence**:\n\n` +
                `📱 **Product**: ${extracted.productName || '❌ Not detected'}\n` +
                `🏢 **Brand**: ${extracted.brand || '❌ Not detected'}\n` +
                `🏪 **Store**: ${extracted.store || '❌ Not detected'}\n` +
                `📅 **Purchase Date**: ${extracted.purchaseDate ? new Date(extracted.purchaseDate).toLocaleDateString('en-IN') : '❌ Not detected'}\n` +
                `⏰ **Warranty Period**: ${extracted.warrantyPeriod || '❌ Not detected'}\n` +
                `🏷️ **Category**: ${extracted.category || '❌ Not detected'}\n\n` +
                `Would you like me to:\n` +
                `✅ **Create warranty** with this information?\n` +
                `✏️ **Edit details** before saving?\n` +
                `🔄 **Try again** with more information?\n` +
                `📝 **Switch to manual form** for precise entry?`,
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '❌ Sorry, I encountered an error processing your request. Please try again or upload a clearer image/document.',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const startVoiceRecognition = () => {
    if (recognitionRef.current && !isListening) {
      setIsListening(true);
      recognitionRef.current.start();
      
      // Add voice indicator message
      const voiceMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'system',
        content: '🎤 Listening... Speak now!',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, voiceMessage]);
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current && isListening) {
      setIsListening(false);
      recognitionRef.current.stop();
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const userId = authService.getUserId();
    
    if (!userId) {
      alert('Please log in to upload files');
      return;
    }

    setIsUploading(true);
    setShowAddMenu(false); // Close menu after action

    // Add file upload message
    const uploadMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: `📄 Uploading: ${file.name}`,
      timestamp: new Date(),
      attachments: [{
        type: file.type.startsWith('image/') ? 'image' : 'file',
        name: file.name,
        url: URL.createObjectURL(file),
        size: file.size
      }]
    };
    setChatMessages(prev => [...prev, uploadMessage]);

    try {
      // Use real document service
      const result: ExtractionResult = await documentService.extractWarranty(file, userId);
      
      setIsUploading(false);
      
      if (result.success && result.warrantyData) {
        const extractedData: ExtractedData = {
          productName: result.warrantyData.productName,
          brand: result.warrantyData.brand,
          store: result.warrantyData.retailer,
          purchaseDate: result.warrantyData.purchaseDate,
          warrantyPeriod: result.warrantyData.warrantyPeriod,
          category: result.warrantyData.category,
          confidence: result.confidence || 0.5,
          source: 'file'
        };
        
        // Show confirmation popup with extracted data
        setExtractedForConfirmation(extractedData);
        setShowConfirmationPopup(true);
      } else {
        // Show error message
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: `❌ Failed to extract warranty information from ${file.name}. ${result.error || 'Please try with a clearer image or different file format.'}`,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      setIsUploading(false);
      console.error('Error processing file:', error);
      
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: `❌ Error processing ${file.name}. Please check your connection and try again.`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleCameraCapture = () => {
    setShowAddMenu(false); // Close menu after action
    cameraInputRef.current?.click();
  };

  const handleOpenManualForm = () => {
    setShowAddMenu(false); // Close menu after action
    setShowManualPopup(true);
  };

  const handleImageCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const userId = authService.getUserId();
      
      if (!userId) {
        alert('Please log in to capture images');
        return;
      }

      setIsUploading(true);

      const uploadMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: `📷 Captured image: ${file.name}`,
        timestamp: new Date(),
        attachments: [{
          type: 'image',
          name: file.name,
          url: URL.createObjectURL(file),
          size: file.size
        }]
      };
      setChatMessages(prev => [...prev, uploadMessage]);

      try {
        // Use real document service for image processing
        const result: ExtractionResult = await documentService.processImage(file, userId);
        
        setIsUploading(false);
        
        if (result.success && result.warrantyData) {
          const extractedData: ExtractedData = {
            productName: result.warrantyData.productName,
            brand: result.warrantyData.brand,
            store: result.warrantyData.retailer,
            purchaseDate: result.warrantyData.purchaseDate,
            warrantyPeriod: result.warrantyData.warrantyPeriod,
            category: result.warrantyData.category,
            confidence: result.confidence || 0.5,
            source: 'image'
          };
          
          // Show confirmation popup with extracted data
          setExtractedForConfirmation(extractedData);
          setShowConfirmationPopup(true);
        } else {
          // Show error message
          const errorMessage: ChatMessage = {
            id: Date.now().toString(),
            type: 'assistant',
            content: `❌ Failed to extract warranty information from the captured image. ${result.error || 'Please try capturing a clearer image with better lighting.'}`,
            timestamp: new Date()
          };
          setChatMessages(prev => [...prev, errorMessage]);
        }
      } catch (error) {
        setIsUploading(false);
        console.error('Error processing captured image:', error);
        
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: '❌ Error processing captured image. Please check your connection and try again.',
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    }
  };

  const handleCreateWarranty = async () => {
    if (!extractedData) return;

    const userId = authService.getUserId();
    
    if (!userId) {
      alert('Please log in to save warranties');
      return;
    }

    try {
      const warrantyData: WarrantyRequest = {
        userId: userId,
        productName: extractedData.productName || '',
        brand: extractedData.brand || '',
        modelNumber: '',
        purchaseDate: extractedData.purchaseDate || '',
        purchaseStore: extractedData.store || '',
        warrantyExpiryDate: '',
        category: extractedData.category as any,
        tags: extractedData.tags,
        entryMethod: 'auto-import'
      };

      if (extractedData.purchaseDate && extractedData.warrantyPeriod) {
        const purchaseDate = new Date(extractedData.purchaseDate);
        const years = extractedData.warrantyPeriod.includes('2') ? 2 : extractedData.warrantyPeriod.includes('3') ? 3 : 1;
        const expiryDate = new Date(purchaseDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + years);
        warrantyData.warrantyExpiryDate = expiryDate.toISOString().split('T')[0];
      }

      await warrantyService.createWarranty(warrantyData);
      
      const successMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: '🎉 **Warranty Created Successfully!**\n\nYour warranty has been added to your portfolio. You can view it in your warranty list.\n\nWould you like to:\n📋 **View warranty details**\n➕ **Add another warranty**\n🏠 **Go to dashboard**',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, successMessage]);
      
      setTimeout(() => {
        navigate('/warranties', { state: { message: 'Warranty added successfully!' } });
      }, 3000);
    } catch (error) {
      console.error('Error creating warranty:', error);
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'assistant',
        content: '❌ Failed to create warranty. Please try again.',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleManualSubmit = async () => {
    const userId = authService.getUserId();
    
    if (!userId) {
      alert('Please log in to save warranties');
      return;
    }

    try {
      const warrantyData: WarrantyRequest = {
        userId: userId,
        productName: manualForm.productName,
        brand: manualForm.brand,
        modelNumber: manualForm.modelNumber,
        purchaseDate: manualForm.purchaseDate,
        purchaseStore: manualForm.purchaseStore,
        warrantyExpiryDate: '',
        category: manualForm.category as any,
        tags: [],
        entryMethod: 'manual'
      };

      // Calculate warranty expiry date
      if (manualForm.purchaseDate && manualForm.warrantyPeriod) {
        const purchaseDate = new Date(manualForm.purchaseDate);
        const yearsMatch = manualForm.warrantyPeriod.match(/(\d+)\s*year/);
        const monthsMatch = manualForm.warrantyPeriod.match(/(\d+)\s*month/);
        
        const expiryDate = new Date(purchaseDate);
        if (yearsMatch) {
          expiryDate.setFullYear(expiryDate.getFullYear() + parseInt(yearsMatch[1]));
        } else if (monthsMatch) {
          expiryDate.setMonth(expiryDate.getMonth() + parseInt(monthsMatch[1]));
        }
        warrantyData.warrantyExpiryDate = expiryDate.toISOString().split('T')[0];
      }

      await warrantyService.createWarranty(warrantyData);
      navigate('/warranties', { state: { message: 'Warranty added successfully!' } });
    } catch (error) {
      console.error('Error creating warranty:', error);
      alert('Failed to create warranty. Please try again.');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  return (
    <div 
      className={`warranty-chat-assistant ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Left Sidebar */}
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-title">
            <button 
              className="back-btn" 
              onClick={() => navigate('/warranties')}
              title="Go back to warranty list"
              aria-label="Navigate back to warranty list"
            >
              <Icon name="arrow-left" />
            </button>
            <div>
              <h2>Suvadi AI</h2>
              <div className="sidebar-subtitle">Smart Assistant</div>
            </div>
          </div>
        </div>

        {/* Chat History */}
        <div className="chat-history">
          <div className="history-section">
            <h3>
              <Icon name="clock" className="tip-icon" />
              Recent Chats
            </h3>
            <div 
              className="history-item"
              title="Continue previous warranty entry for Samsung Galaxy M31"
              role="button"
              tabIndex={0}
              aria-label="Resume Samsung Galaxy M31 warranty entry from 2 hours ago"
            >
              <div className="history-item-title">Samsung Galaxy M31</div>
              <div className="history-item-time">2 hours ago</div>
            </div>
            <div 
              className="history-item"
              title="Continue previous warranty entry for iPhone 13 Pro"
              role="button"
              tabIndex={0}
              aria-label="Resume iPhone 13 Pro warranty entry from yesterday"
            >
              <div className="history-item-title">iPhone 13 Pro</div>
              <div className="history-item-time">Yesterday</div>
            </div>
            <div 
              className="history-item"
              title="Continue previous warranty entry for LG Washing Machine"
              role="button"
              tabIndex={0}
              aria-label="Resume LG Washing Machine warranty entry from 3 days ago"
            >
              <div className="history-item-title">LG Washing Machine</div>
              <div className="history-item-time">3 days ago</div>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="tips-section">
          <h3>
            <Icon name="star" className="tip-icon" />
            Quick Tips
          </h3>
          <div className="tip-item" title="Type naturally like talking to a friend - no rigid forms needed">
            <Icon name="message-circle" className="tip-icon" />
            <div className="tip-text">Type naturally: "Bought Samsung phone today with 2yr warranty"</div>
          </div>
          <div className="tip-item" title="Click the microphone button to speak your warranty details">
            <Icon name="mic" className="tip-icon" />
            <div className="tip-text">Use voice commands for hands-free entry</div>
          </div>
          <div className="tip-item" title="Take photos of receipts or warranty cards for instant data extraction">
            <Icon name="ocr-camera" className="tip-icon" />
            <div className="tip-text">Snap photos of receipts for instant processing</div>
          </div>
          <div className="tip-item" title="Upload documents like PDFs, images, or warranty cards">
            <Icon name="file-plus" className="tip-icon" />
            <div className="tip-text">Drag & drop files for automatic data extraction</div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-main-area">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-title">
            Add Warranty
          </div>
          <div className="status-indicator">
            <div className="status-dot"></div>
            <span>AI Ready</span>
          </div>
        </div>

        {/* Chat Messages Container */}
        <div className="chat-messages-container">
          {/* AI Chat Interface */}
          <div className="chat-messages-area">
            {chatMessages.map((message) => (
              <div key={message.id} className={`chat-message ${message.type}`}>
                <div className="message-avatar">
                  {message.type === 'user' ? (
                    <Icon name="user" />
                  ) : message.type === 'assistant' ? (
                    <Icon name="bot" />
                  ) : (
                    <Icon name="star" />
                  )}
                </div>
                <div className="message-content">
                  <div className="message-text">{message.content}</div>
                  {message.attachments && (
                    <div className="message-attachments">
                      {message.attachments.map((attachment, idx) => (
                        <div key={idx} className="attachment">
                          {attachment.type === 'image' ? (
                            <img src={attachment.url} alt={attachment.name} className="attachment-image" />
                          ) : (
                            <div className="attachment-file">
                              <Icon name="file" />
                              <span>{attachment.name}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString('en-IN', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
            ))}
            
            {isProcessing && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <Icon name="bot" />
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span>AI is processing...</span>
                  </div>
                </div>
              </div>
            )}

            {isUploading && (
              <div className="chat-message assistant">
                <div className="message-avatar">
                  <Icon name="bot" />
                </div>
                <div className="message-content">
                  <div className="upload-progress">
                    <Icon name="upload" className="upload-icon" />
                    <span>Processing your document...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={chatEndRef} />
          </div>

          {/* Voice Indicator */}
          {isListening && (
            <div className="voice-indicator-inline">
              <div className="pulse-dot"></div>
              <span>Listening... Speak now</span>
              <button onClick={stopVoiceRecognition} className="stop-voice-btn">
                <Icon name="x" />
              </button>
            </div>
          )}

          {/* Enhanced Input Area */}
          <div className="chat-input-enhanced">
            <div className="input-container">
              {/* Hidden helper text for screen readers */}
              <div id="input-help-text" className="sr-only">
                This is an AI-powered warranty entry system. You can type naturally, use voice commands, take photos, or upload documents. 
                The AI will automatically extract product details, purchase information, and warranty period from your input.
              </div>
              
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type naturally: 'I bought a Samsung Galaxy M31 at Reliance Digital today with 2 years warranty'"
                className="chat-input-main"
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isProcessing || isUploading}
                title="Enter warranty details naturally - AI will extract product, store, date, and warranty period"
                aria-label="Warranty entry chat input - type or speak your warranty details naturally"
                aria-describedby="input-help-text"
              />
              
              {/* Action Buttons */}
              <div className="input-actions">
                {/* Voice Button */}
                <button 
                  className={`action-btn voice-btn ${isListening ? 'listening' : ''}`}
                  onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
                  disabled={isProcessing || isUploading}
                  title={isListening ? "Stop voice recording and process speech" : "Start voice recording - speak your warranty details"}
                  aria-label={isListening ? "Stop voice recognition" : "Start voice recognition to speak warranty details"}
                >
                  <Icon name={isListening ? "mic-off" : "mic"} />
                </button>

                {/* Add Menu Button */}
                <div className="add-menu-container">
                  <button 
                    className={`action-btn add-btn ${showAddMenu ? 'active' : ''}`}
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    disabled={isProcessing || isUploading}
                    title="Add warranty using different methods - camera, upload, or manual entry"
                    aria-label="Open add warranty options menu"
                  >
                    <Icon name="plus" />
                  </button>

                  {/* Add Menu Dropdown */}
                  {showAddMenu && (
                    <div className="add-menu-dropdown">
                      <button 
                        className="menu-item"
                        onClick={handleCameraCapture}
                        title="Take a photo of receipt or warranty card"
                        aria-label="Add from camera"
                      >
                        <Icon name="ocr-camera" />
                        <span>Add from Camera</span>
                      </button>
                      
                      <button 
                        className="menu-item"
                        onClick={() => {
                          setShowAddMenu(false);
                          fileInputRef.current?.click();
                        }}
                        title="Upload invoice, receipt, or warranty document"
                        aria-label="Upload invoice"
                      >
                        <Icon name="file-plus" />
                        <span>Upload Invoice</span>
                      </button>
                      
                      <button 
                        className="menu-item"
                        onClick={handleOpenManualForm}
                        title="Fill warranty details manually using a form"
                        aria-label="Add manually"
                      >
                        <Icon name="manage" />
                        <span>Add Manually</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Send Button */}
                <button 
                  className="action-btn send-btn"
                  onClick={() => handleSendMessage()}
                  disabled={isProcessing || isUploading || !inputText.trim()}
                  title={!inputText.trim() ? "Enter some text first to send message" : "Send message to AI for warranty processing"}
                  aria-label={!inputText.trim() ? "Send button disabled - enter text first" : "Send warranty details to AI assistant"}
                >
                  <Icon name="send" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Drag & Drop Indicator */}
        {dragActive && (
          <div className="drag-overlay">
            <div className="drag-content">
              <Icon name="upload-cloud" className="drag-icon" />
              <h3>Drop your files here</h3>
              <p>Receipts, warranty cards, or invoices</p>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        onChange={(e) => handleFileUpload(e.target.files)}
        style={{ display: 'none' }}
        title="Select warranty documents, receipts, or images to upload"
        aria-label="File upload input for warranty documents - supports PDF, JPG, PNG, HEIC formats"
      />
      
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleImageCapture}
        style={{ display: 'none' }}
        title="Capture photo using device camera"
        aria-label="Camera input to capture photos of warranty documents or receipts"
      />

      {/* Quick Action Buttons for Extracted Data */}
      {extractedData && (
        <div className="quick-actions-overlay">
          <div className="quick-actions-content">
            <h4>🤖 AI Extracted Data Ready!</h4>
            <div className="action-buttons">
              <button className="btn-create" onClick={handleCreateWarranty}>
                ✅ Create Warranty
              </button>
              <button className="btn-edit" onClick={() => setExtractedData(null)}>
                ✏️ Edit Details
              </button>
              <button className="btn-manual" onClick={() => {
                setExtractedData(null);
                handleSendMessage("Let me fill out a manual form step by step", 'manual');
              }}>
                📝 Manual Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Popup */}
      {showManualPopup && (
        <div className="popup-overlay" onClick={() => setShowManualPopup(false)}>
          <div className="popup-content manual-form-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Manual Warranty Entry</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowManualPopup(false)}
                title="Close manual entry form"
                aria-label="Close manual entry popup"
              >
                <Icon name="x" />
              </button>
            </div>
            
            <div className="popup-body">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="productName">Product Name *</label>
                  <input
                    id="productName"
                    type="text"
                    value={manualForm.productName}
                    onChange={(e) => setManualForm({...manualForm, productName: e.target.value})}
                    placeholder="e.g., Samsung Galaxy M31"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="brand">Brand *</label>
                  <input
                    id="brand"
                    type="text"
                    value={manualForm.brand}
                    onChange={(e) => setManualForm({...manualForm, brand: e.target.value})}
                    placeholder="e.g., Samsung"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="modelNumber">Model Number</label>
                  <input
                    id="modelNumber"
                    type="text"
                    value={manualForm.modelNumber}
                    onChange={(e) => setManualForm({...manualForm, modelNumber: e.target.value})}
                    placeholder="e.g., SM-M315F"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchaseDate">Purchase Date *</label>
                  <input
                    id="purchaseDate"
                    type="date"
                    value={manualForm.purchaseDate}
                    onChange={(e) => setManualForm({...manualForm, purchaseDate: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchaseStore">Purchase Store</label>
                  <input
                    id="purchaseStore"
                    type="text"
                    value={manualForm.purchaseStore}
                    onChange={(e) => setManualForm({...manualForm, purchaseStore: e.target.value})}
                    placeholder="e.g., Reliance Digital"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="warrantyPeriod">Warranty Period *</label>
                  <select
                    id="warrantyPeriod"
                    value={manualForm.warrantyPeriod}
                    onChange={(e) => setManualForm({...manualForm, warrantyPeriod: e.target.value})}
                  >
                    <option value="">Select warranty period</option>
                    <option value="6 months">6 months</option>
                    <option value="1 year">1 year</option>
                    <option value="2 years">2 years</option>
                    <option value="3 years">3 years</option>
                    <option value="5 years">5 years</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    value={manualForm.category}
                    onChange={(e) => setManualForm({...manualForm, category: e.target.value})}
                  >
                    <option value="">Select category</option>
                    <option value="electronics">Electronics</option>
                    <option value="appliances">Appliances</option>
                    <option value="automotive">Automotive</option>
                    <option value="furniture">Furniture</option>
                    <option value="clothing">Clothing</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group form-group-full">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    value={manualForm.notes}
                    onChange={(e) => setManualForm({...manualForm, notes: e.target.value})}
                    placeholder="Any additional notes about the warranty..."
                    rows={3}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="popup-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowManualPopup(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  handleManualSubmit();
                  setShowManualPopup(false);
                }}
                disabled={!manualForm.productName || !manualForm.brand || !manualForm.purchaseDate || !manualForm.warrantyPeriod}
              >
                Save Warranty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Popup for Extracted Data */}
      {showConfirmationPopup && extractedForConfirmation && (
        <div className="popup-overlay" onClick={() => setShowConfirmationPopup(false)}>
          <div className="popup-content confirmation-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>🤖 Data Extracted Successfully!</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowConfirmationPopup(false)}
                title="Close confirmation popup"
                aria-label="Close data confirmation popup"
              >
                <Icon name="x" />
              </button>
            </div>
            
            <div className="popup-body">
              <p>I've extracted the following information from your {extractedForConfirmation.source}. Please review and edit if needed:</p>
              
              <div className="extracted-data-preview">
                <div className="data-item">
                  <label>Product Name:</label>
                  <input
                    type="text"
                    value={extractedForConfirmation.productName || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      productName: e.target.value
                    })}
                  />
                </div>
                
                <div className="data-item">
                  <label>Brand:</label>
                  <input
                    type="text"
                    value={extractedForConfirmation.brand || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      brand: e.target.value
                    })}
                  />
                </div>
                
                <div className="data-item">
                  <label>Store:</label>
                  <input
                    type="text"
                    value={extractedForConfirmation.store || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      store: e.target.value
                    })}
                  />
                </div>
                
                <div className="data-item">
                  <label>Purchase Date:</label>
                  <input
                    type="date"
                    value={extractedForConfirmation.purchaseDate || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      purchaseDate: e.target.value
                    })}
                  />
                </div>
                
                <div className="data-item">
                  <label>Warranty Period:</label>
                  <select
                    value={extractedForConfirmation.warrantyPeriod || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      warrantyPeriod: e.target.value
                    })}
                  >
                    <option value="">Select warranty period</option>
                    <option value="6 months">6 months</option>
                    <option value="1 year">1 year</option>
                    <option value="2 years">2 years</option>
                    <option value="3 years">3 years</option>
                    <option value="5 years">5 years</option>
                  </select>
                </div>
                
                <div className="data-item">
                  <label>Category:</label>
                  <select
                    value={extractedForConfirmation.category || ''}
                    onChange={(e) => setExtractedForConfirmation({
                      ...extractedForConfirmation,
                      category: e.target.value
                    })}
                  >
                    <option value="">Select category</option>
                    <option value="electronics">Electronics</option>
                    <option value="appliances">Appliances</option>
                    <option value="automotive">Automotive</option>
                    <option value="furniture">Furniture</option>
                    <option value="clothing">Clothing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="confidence-indicator">
                <span>Extraction Confidence: {Math.round(extractedForConfirmation.confidence * 100)}%</span>
                <div className="confidence-bar">
                  <div 
                    className="confidence-fill" 
                    style={{ width: `${extractedForConfirmation.confidence * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="popup-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowConfirmationPopup(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={async () => {
                  setExtractedData(extractedForConfirmation);
                  setShowConfirmationPopup(false);
                  await handleCreateWarranty();
                }}
              >
                Create Warranty
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddWarrantyPage;
